/**
 * Week view: robots down, days across. Each cell is that robot's day as a
 * mini strip (the working hours, or the whole day with "24 h") and the
 * number of tasks; the number turns red when something did not finish.
 * A cell is a button: it opens the Day view for that robot and day.
 *
 * The strips are pictures; every cell's button says the same thing in
 * words ("NomNom, Thursday 22/Oct/2026: 9 tasks, 1 didn't finish"), and
 * the cells are one roving tab stop moved with the arrow keys.
 */
import { memo, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { weekCell, type LaneBoard, type LaneRow, type WeekCell } from './lane-layout';
import { laneVars } from './lane-style';
import {
  WEEKDAY_LONG,
  WORK_END_HOUR,
  WORK_START_HOUR,
  dayWindow,
  formatKey,
  instantAt,
  shortDayLabel,
  weekDays,
  weekdayOf,
  type DateKey,
} from './lane-time';
import type { SiteBar } from './site-row';

export interface WeekLanesProps {
  dateKey: DateKey;
  todayKey: DateKey;
  timeZone: string;
  fullDay: boolean;
  board: LaneBoard;
  siteBars: readonly SiteBar[];
  collapsed: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  /** Open the Day view for `day`, filtered to `robot` when one is named. */
  onOpenDay: (day: DateKey, robot: string | null) => void;
}

function tasks(n: number): string {
  return n === 1 ? '1 task' : `${n} tasks`;
}

/** The full sentence, for the tooltip and the screen reader. */
function sentence(count: number, fails: number): string {
  return `${tasks(count)}${fails ? `, ${fails} didn’t finish` : ''}`;
}

/** A day or group total in a form that fits a seventh of a laptop:
 *  "234 tasks", or "234 · 10 not done" with the not-done part red. */
function Total({ count, fails }: { count: number; fails: number }) {
  if (!fails) return <>{tasks(count)}</>;
  return (
    <>
      {count} · <span className="nx-week__fail">{fails} not done</span>
    </>
  );
}

export function WeekLanes(props: WeekLanesProps) {
  const { dateKey, todayKey, timeZone, fullDay, board, siteBars, collapsed } = props;
  const days = useMemo(() => weekDays(dateKey), [dateKey]);
  const windows = useMemo(() => days.map((d) => dayWindow(d, timeZone)), [days, timeZone]);
  const strips = useMemo(
    () =>
      days.map((d, i) =>
        fullDay
          ? windows[i]!
          : {
              fromMs: instantAt(d, WORK_START_HOUR, timeZone),
              toMs: instantAt(d, WORK_END_HOUR, timeZone),
            },
      ),
    [days, windows, fullDay, timeZone],
  );

  const rows = useMemo(() => {
    const out: LaneRow[] = [];
    if (board.topAny) out.push(board.topAny);
    for (const g of board.groups) if (!collapsed.has(g.id)) out.push(...g.rows);
    return out;
  }, [board, collapsed]);

  const cells = useMemo(() => {
    const m = new Map<string, WeekCell[]>();
    for (const r of rows)
      m.set(
        r.id,
        windows.map((w, i) => weekCell(r.items, w, strips[i]!)),
      );
    return m;
  }, [rows, windows, strips]);

  const groupTotals = useMemo(
    () =>
      new Map(
        board.groups.map((g) => {
          const items = g.rows.flatMap((r) => r.items);
          return [
            g.id,
            windows.map((w, i) => {
              const c = weekCell(items, w, strips[i]!);
              return { count: c.count, fails: c.didntFinish };
            }),
          ] as const;
        }),
      ),
    [board, windows, strips],
  );

  const dayTotals = useMemo(() => {
    const all = [
      ...(board.topAny?.items ?? []),
      ...board.groups.flatMap((g) => g.rows.flatMap((r) => r.items)),
    ];
    return windows.map((w, i) => {
      const c = weekCell(all, w, strips[i]!);
      return { count: c.count, fails: c.didntFinish };
    });
  }, [board, windows, strips]);

  const siteCells = useMemo(
    () =>
      windows.map((w, i) => {
        const s = strips[i]!;
        const span = s.toMs - s.fromMs;
        return siteBars
          .filter((b) => b.startMs < w.toMs && b.endMs > w.fromMs)
          .map((b) => {
            const from = Math.min(Math.max(b.startMs, s.fromMs), s.toMs);
            const to = Math.min(Math.max(b.endMs, s.fromMs), s.toMs);
            const width = Math.max(1.5, ((to - from) / span) * 100);
            return { bar: b, left: Math.min(((from - s.fromMs) / span) * 100, 100 - width), width };
          });
      }),
    [siteBars, windows, strips],
  );

  // ---- roving focus over the cells ---------------------------------------
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [focus, setFocus] = useState<[number, number]>([0, 0]);
  const navCount = rows.length + 1; // + the Site row
  const [fr, fc] = focus[0] < navCount ? focus : [0, focus[1]];
  const move = (r: number, c: number) => {
    const rr = Math.max(0, Math.min(navCount - 1, r));
    const cc = Math.max(0, Math.min(6, c));
    setFocus([rr, cc]);
    gridRef.current?.querySelector<HTMLElement>(`[data-nav="${rr}:${cc}"]`)?.focus();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const nav = (e.target as HTMLElement).dataset?.nav;
    if (!nav) return;
    const [r, c] = nav.split(':').map(Number) as [number, number];
    const step: Record<string, [number, number]> = {
      ArrowRight: [r, c + 1],
      ArrowLeft: [r, c - 1],
      ArrowDown: [r + 1, c],
      ArrowUp: [r - 1, c],
      Home: [r, 0],
      End: [r, 6],
    };
    const to = step[e.key];
    if (!to) return;
    e.preventDefault();
    move(to[0], to[1]);
  };

  const dayName = (d: DateKey) => `${WEEKDAY_LONG[weekdayOf(d)]} ${formatKey(d)}`;
  let navRow = 1;
  const anyNav = board.topAny ? navRow++ : -1;

  return (
    <div className="nx-lanes__scroll nx-lanes__scroll--week" data-testid="cal-week-scroller">
      <div
        className="nx-week"
        ref={gridRef}
        role="grid"
        aria-label="Tasks by robot and day"
        onKeyDown={onKeyDown}
        data-testid="cal-week-lanes"
      >
        <div className="nx-week__row nx-week__row--head" role="row">
          <div className="nx-week__name nx-lanes__corner" role="columnheader">
            {board.robotCount === 1 ? '1 robot' : `${board.robotCount} robots`}
          </div>
          {days.map((d, i) => (
            <div
              key={d}
              role="columnheader"
              className={`nx-week__day-head${d === todayKey ? ' is-today' : ''}`}
              data-testid={`cal-week-day-${d}`}
            >
              <div className="nx-week__day-name">
                {shortDayLabel(d)}
                {d === todayKey ? ' · today' : ''}
              </div>
              <div
                className="nx-week__day-sum"
                title={sentence(dayTotals[i]!.count, dayTotals[i]!.fails)}
                aria-label={sentence(dayTotals[i]!.count, dayTotals[i]!.fails)}
              >
                <Total count={dayTotals[i]!.count} fails={dayTotals[i]!.fails} />
              </div>
            </div>
          ))}
        </div>

        <div className="nx-week__row nx-week__row--site" role="row" data-testid="cal-week-site-row">
          <div className="nx-week__name" role="rowheader">
            Site
          </div>
          {days.map((d, i) => {
            const bars = siteCells[i]!;
            const words = bars.length ? bars.map((b) => b.bar.label).join(', ') : 'nothing blocked';
            return (
              <button
                key={d}
                type="button"
                role="gridcell"
                className={`nx-week__cell${d === todayKey ? ' is-today' : ''}`}
                data-nav={`0:${i}`}
                tabIndex={fr === 0 && fc === i ? 0 : -1}
                aria-label={`Site, ${dayName(d)}: ${words}. Open this day.`}
                onFocus={() => setFocus([0, i])}
                onClick={() => props.onOpenDay(d, null)}
              >
                <span className="nx-week__strip" aria-hidden="true">
                  {bars.map((b) => (
                    <span
                      key={b.bar.id}
                      className={`nx-week__site-block nx-week__site-block--${b.bar.kind}${b.bar.liftRule?.kind === 'allow_travel' ? ' is-allow' : ''}`}
                      style={{ left: `${b.left}%`, width: `${b.width}%` }}
                    />
                  ))}
                </span>
                <span className="nx-week__count" aria-hidden="true">
                  {bars.length ? bars.length : ''}
                </span>
              </button>
            );
          })}
        </div>

        {board.topAny && (
          <WeekRow
            row={board.topAny}
            cells={cells.get(board.topAny.id)!}
            days={days}
            todayKey={todayKey}
            navRow={anyNav}
            focusCol={fr === anyNav ? fc : -1}
            onFocusCell={setFocus}
            onOpenDay={props.onOpenDay}
          />
        )}

        {board.groups.map((g) => {
          const closed = collapsed.has(g.id);
          const totals = groupTotals.get(g.id)!;
          return (
            <div key={g.id} role="rowgroup">
              <div className="nx-week__row nx-week__row--group" role="row">
                <button
                  type="button"
                  className="nx-week__name nx-week__group-head"
                  aria-expanded={!closed}
                  onClick={() => props.onToggleGroup(g.id)}
                  data-testid={`cal-group-${g.id}`}
                >
                  <span aria-hidden="true">{closed ? '▸' : '▾'}</span> {g.name} · {g.robotCount}
                </button>
                {totals.map((t, i) => (
                  <span
                    key={days[i]}
                    role="gridcell"
                    className="nx-week__group-total"
                    title={`${g.name}: ${sentence(t.count, t.fails)}`}
                    aria-label={`${g.name}, ${dayName(days[i]!)}: ${sentence(t.count, t.fails)}`}
                  >
                    <Total count={t.count} fails={t.fails} />
                  </span>
                ))}
              </div>
              {!closed &&
                g.rows.map((r) => {
                  const idx = navRow++;
                  return (
                    <WeekRow
                      key={r.id}
                      row={r}
                      cells={cells.get(r.id)!}
                      days={days}
                      todayKey={todayKey}
                      navRow={idx}
                      focusCol={fr === idx ? fc : -1}
                      onFocusCell={setFocus}
                      onOpenDay={props.onOpenDay}
                    />
                  );
                })}
            </div>
          );
        })}

        {board.groups.length === 0 && !board.topAny && (
          <p className="nx-lanes__empty" data-testid="cal-lanes-empty">
            No robots match. Clear “Find robot” to see every lane.
          </p>
        )}
      </div>
    </div>
  );
}

interface WeekRowProps {
  row: LaneRow;
  cells: WeekCell[];
  days: DateKey[];
  todayKey: DateKey;
  navRow: number;
  focusCol: number;
  onFocusCell: (rc: [number, number]) => void;
  onOpenDay: (day: DateKey, robot: string | null) => void;
}

const WeekRow = memo(function WeekRow({
  row,
  cells,
  days,
  todayKey,
  navRow,
  focusCol,
  onFocusCell,
  onOpenDay,
}: WeekRowProps) {
  // "Any cleaning robot" for an Any-robot row (`anyRowLabel`).
  const who = row.label;
  return (
    <div
      className={`nx-week__row${row.kind === 'any' ? ' nx-week__row--any' : ''}`}
      role="row"
      data-testid={`cal-week-${row.id}`}
    >
      <div
        className="nx-week__name"
        role="rowheader"
        title={row.robot ? `${row.robot.name} · ${row.robot.fleet}` : undefined}
      >
        {who}
      </div>
      {cells.map((c, i) => {
        const d = days[i]!;
        const words = `${who}, ${WEEKDAY_LONG[weekdayOf(d)]} ${formatKey(d)}: ${tasks(c.count)}${
          c.didntFinish ? `, ${c.didntFinish} didn’t finish` : ''
        }. Open this day.`;
        return (
          <button
            key={d}
            type="button"
            role="gridcell"
            className={`nx-week__cell${d === todayKey ? ' is-today' : ''}`}
            data-nav={`${navRow}:${i}`}
            tabIndex={focusCol === i ? 0 : -1}
            aria-label={words}
            title={words}
            onFocus={() => onFocusCell([navRow, i])}
            onClick={() => onOpenDay(d, row.kind === 'robot' ? row.robot!.name : null)}
            data-testid={`cal-week-cell-${row.id}-${d}`}
          >
            <span className="nx-week__strip" aria-hidden="true">
              {c.blocks.map((b) => (
                <span
                  key={b.id}
                  className="nx-week__block"
                  data-outcome={b.outcome}
                  style={{ left: `${b.left}%`, width: `${b.width}%`, ...laneVars(b.lane) }}
                />
              ))}
            </span>
            <span
              className={`nx-week__count${c.didntFinish ? ' has-fail' : ''}`}
              aria-hidden="true"
              data-testid={`cal-week-count-${row.id}-${d}`}
            >
              {c.count ? c.count : '—'}
            </span>
          </button>
        );
      })}
    </div>
  );
});
